import * as _ from 'lodash'
import { all, and, Variable, VArray, VBoolean } from 'alkali'
import {
	VExtraction,
	ReferencedTerms,
	LoadedExtraction,
	comparisonState,
	deriveConditions,
	loadResults,
	loadExtraction,
	submitExtractions, newExtraction, getReferencedTerms, detectTermPresence, queryAnnotationsForType, altsForSelectedRow,
	inferExtractState
} from './extraction'
import { ExtractionComparison, compareExtraction, detectOverwrite, detectInvalidNewGroups } from './comparison'
import type { Extraction, ERow, EGroup, DAStudyGroup, StudyAnnotations } from 'services/extract/types'
import { extract, createExtractionUpdate, rowId } from 'services/extract/generate'
import { Abstract } from './Abstract'
import AnnotatedAbstract from './AnnotatedAbstract'
import StudyContext from './StudyContext'
import StudySummaryModel from './StudySummaryModel'
import UpdateModel, { createExtractionRequest, generateModelUpdates } from './UpdateModel'
import { deleteRequest, post } from 'tools/request'
import { Validation, validResult } from './validation'
import { studyDetailsPane } from 'components/studies/StudyDetailsPane'

/**
 * Base UI model for interacting with extraction. Executes abstract annotation and then custom initialization.
 */
export abstract class BaseExtractionModel implements UpdateModel {
	static GROUP_ADD_DISALLOWED = 'Cannot add new groups'
	readonly study: StudyContext
	readonly loaded = new Variable<LoadedExtraction>()
	readonly extraction = new VExtraction()
	initialGroups: EGroup[] = []
	initialRows: ERow[] = []
	readonly extractions: VArray<any>
	readonly loading = new VBoolean(true)
	readonly referencedTerms = new Variable<ReferencedTerms>()
	readonly comparison: Variable<ExtractionComparison>
	readonly status: Variable<ReturnType<typeof comparisonState>>
	readonly hasChanges: VBoolean
	readonly isValid: VBoolean
	readonly isSelfValid: VBoolean
	readonly addValidation: Variable<Validation>
	readonly errors: Variable<string[]>
	readonly canSubmit: VBoolean
	readonly whenLoaded = Promise.resolve(null)

	//use to let ExtractionButton know whether user arrived at EDIT via History or directly.
	readonly fromHistory: VBoolean

	constructor(readonly summary: StudySummaryModel, readonly onSubmit?) {
		this.study = summary.studyCtx
		this.extractions = new VArray(this.study.extractions || [])
		this.fromHistory = new VBoolean(false)

		// fetch annotations and init only if we are actually extracting (have submit callback)
		if (onSubmit) {
			let resolve
			this.whenLoaded = new Promise(r => resolve = r)
			this.summary.annotations.then(annotations => {
				try {
					this.initialize(annotations)
				} finally {
					resolve()
				}
			}).then(() => this.loading.put(false))
		}

		this.comparison = this.extraction.to((e: Extraction) => compareExtraction(this.study, e))
		// if we are in Add mode, ensure that either we have a new outcome and all existing or new groups
		// or we have an existing outcome with new groups
		this.addValidation = this.initAddValidation()
		this.status = all([this.comparison, this.addValidation], ({ comparisons }, addValidation) => {
			const state = comparisonState(comparisons)
			state.errors.push(...addValidation)
			return state
		})
		this.isSelfValid = all([this.extraction.validation, this.status, this.addValidation], (extractValidation, status, addValidation) =>
			extractValidation.isValid && !status.error && addValidation.isValid
		)
		this.errors = all([this.summary.abstractSource.validation, this.extraction.validation, this.status, this.addValidation], (summaryValid, extractValid, status, addValid) =>
			_.compact(_.uniq([
				...status.errors,
				...addValid,
				...(summaryValid.isValid ? [] : summaryValid),
				...(extractValid.isValid ? [] : extractValid)
			]))
		)
		this.isValid = and(this.summary.isValid, this.isSelfValid)

		// for now assume any valid input on the extract tab introduces submittable changes
		//this.hasChanges = this.isSelfValid

		//quickly calculate if there are any changes
		this.hasChanges = this.extraction.to( (e: Extraction) => {

			//only check for changes if data is valid
			var chkValid = this.extraction.validation.valueOf().isValid

			if (chkValid) {
				const extractTime = Date.now()
				var updates = this.generateUpdates(extractTime, true)
				if (updates.updates?.length > 0)
					return true
				else
					return false
			}
			else
				return this.isSelfValid
	   })

		this.canSubmit = this.isValid
	}

	/**
	 * Perform initialization given the abstract/title annotations
	 */
	protected abstract initialize(annotations: StudyAnnotations): void

	/**
	 * Returns reactive Validation that determines whether we are allow to perform an Add operation or not
	 */
	protected abstract initAddValidation(): Variable<Validation>

	protected initExtraction(loaded: LoadedExtraction, defaultOutcome = false) {
		this.loaded.put(loaded)
		const extraction = newExtraction(loaded)
		this.initialGroups = extraction.groups
		this.initialRows = extraction.studyLevel.concat(extraction.characteristics, extraction.groupLevel, extraction.outcomes)
		if (!extraction.outcomes.length && defaultOutcome) {
			extraction.outcomes.push({
				id: rowId(),
				section: 'Outcomes',
				type: 'Binary',
				rowType: undefined,
				name: {
					val: ''
				}
			})
		}
		this.extraction.put(extraction)
		this.referencedTerms.put(getReferencedTerms(loaded))
	}

	/**
	 * Generates updates from the StudySummaryModel and self
	 */
	generateUpdates(id, checkOnly: boolean = false) {
		const extraction = this.extraction.valueOf() as Extraction
		const conditions = deriveConditions(this.study.queryAnnotations.valueOf(),  this.study.searchedConditions.map(altsForSelectedRow), this.study.study)
		const extractions = extract(this.summary.effectiveStudy, id, extraction, this.comparison.valueOf(), conditions, checkOnly)
		const meta = {
			Note: extraction.note,
			ImportSource: extraction.importSource
		}
		console.log(extractions)
		return {
			meta,
			updates: createExtractionUpdate(id, extractions),
			generated: extractions.generated
		}
	}

	/**
	 * Submits the extraction
	 */
	submit(debug = false, collection?: string) {
		const generatedExtraction = generateModelUpdates(this.summary, this)
		const newOutcomes = _.compact(generatedExtraction.updates.flatMap(u => u.generated?.sections?.Outcomes?.filter(e => !e.edit && e.generate).map(e => e.source.name?.term)))
		let mismatchOutcomes = []
		if (newOutcomes) {
			const result = detectTermPresence(
				newOutcomes,
				this.summary.studyCtx.outcomes,
				this.summary.annotations.valueOf()?.abstract,
				queryAnnotationsForType(this.summary.studyCtx.queryAnnotations.valueOf(), 'outcome'),
				this.summary.studyCtx.searchedOutcomes.map(altsForSelectedRow)
			)
			if (!result.matchesQuery) {
				mismatchOutcomes = newOutcomes.map(term => term)
			}
		}
		const req = createExtractionRequest(this.summary.effectiveStudy, this.study.user, generatedExtraction)
		collection = (collection || '').trim()
		if (collection) {
			req.Updates.forEach(u => u.Collection = collection)
		}
		return submitExtractions(req, () => this.onSubmit?.(mismatchOutcomes))
	}

	/**
	 * Deletes an extraction
	 */
	deleteExtraction(extraction, debug = false) {
		const i = this.extractions.indexOf(extraction)
		return deleteRequest(`/portal/extract/${this.study.study?.id || this.summary.study.id}/${extraction.Id || extraction.Date}`).then(result => {
			this.extractions.splice(i, 1)
			this.extractions.updated()

            //need to update dependencies
            const extractDeps = this.study.study.metadata.ExtractDeps

            //remove dependencies for deleted item
			var deleteId = extraction.Id
			_.forEach(extractDeps, dep => {
                if (dep?.dependents)
                    dep.dependents = dep.dependents.filter(d => d.extraction != deleteId)  
            })

			//reload study
			const extractState = inferExtractState(this.study, [], null, app.user)
			studyDetailsPane.showStudy(this.study, extractState)

			if (this.fromHistory.valueOf() == true)
				this.clearValues()

			//if (!debug) this.onSubmit && this.onSubmit()
		})
	}

	/**
	 * Submits an administrative "split" of an existing extraction
	 */
	splitExtraction(extraction, debug = false) {
		const i = this.extractions.indexOf(extraction)
		return post(`/portal/split-extraction/${this.summary.study.id}/${extraction.Id}`).then(result => {
			this.extractions.updated()
			if (!debug) this.onSubmit && this.onSubmit()
		})
	}
}

/**
 * The cell-extraction model. Initialized from search/selected outcomes/groups, and initializes an annotated abstract
 * from which it also optionally (based on the AutoRapidExtractValues option) automatically attempts to extract values
 */
export default class ExtractionModel extends BaseExtractionModel {
	readonly editingExtraction: VBoolean
	readonly editToggle: VBoolean
	readonly annotatedAbstract: Variable<AnnotatedAbstract>
	invalidOverwrite: Variable<boolean>
	invalidNewGroups: Variable<EGroup[]>

	constructor(summary: StudySummaryModel, readonly analysisType, readonly selectedOutcome, readonly selectedGroups: DAStudyGroup[], onSubmit?, onlyPICO = false) {
		super(summary, onSubmit)
		// we're editing if we have loaded data into the extraction (either via existing outcome/group selection, or loaded from extraction history)
		this.editingExtraction = this.loaded.property('loaded')
		// define a toggle between Add and Edit mode; the reverse transform clears the extraction when transitioning
		// from edit to add mode
		this.editToggle = this.editingExtraction.to(e => !!e, (val, index) => {
			if (val == false) {
				// if we are manually switching to Add from Edit, clear the loaded extraction data
				this.clearValues()
			}
		})

		this.annotatedAbstract = all([this.summary.abstract, summary.annotations], (abstract: Abstract, annotations: StudyAnnotations) =>
			this.referencedTerms.to(referencedTerms =>
				all([summary.allToggle, summary.editingAbstract], (outcomes, showAll, editingAbstract) => {
					const annotated = new AnnotatedAbstract(abstract, annotations.abstract, referencedTerms, showAll, onlyPICO)
					this.onAbstractAnnotation(annotated, editingAbstract)
					return annotated
				})
			)
		)
	}

	/**
	 * Initialize the Extraction from search/selected outcomes/groups
	 */
	protected initialize(annotations) {
		const loaded = loadResults(this.analysisType, this.selectedOutcome, this.selectedGroups, this.study.searchedOutcomes, this.study.searchedItvns, this.summary.studyCtx.allOutcomes, annotations)
		this.initExtraction(loaded, true)
	}

	/**
	 * If we are not editing, but adding a new extraction, and AutoRapidExtractValues is enabled, make an attempt at
	 * "auto-extracting"
	 */
	protected onAbstractAnnotation(annotated: AnnotatedAbstract, editingAbstract: boolean) {
		if (!editingAbstract && !this.editingExtraction.valueOf() && this.study.user.AutoRapidExtractValues) {
			// only attempt auto-extract when adding new extraction, not when editing existing selected outcome
			if (annotated.autoExtract(this.extraction.valueOf())) {
				this.extraction.updated()
			}
		}
	}

	/**
	 * Return the Add validation: if we are Adding, allow if we are not overwriting existing cells (which would be an edit)
	 * and we are not adding groups for a study which is ineligible for group addition
	 */
	protected initAddValidation() {
		const loaded = this.loaded.property('loaded')
		const vars = all([loaded, this.extraction, this.comparison] as const)
		// either we are editing (we have loaded data), or...
		this.invalidOverwrite = vars.to(([loaded, extraction, comparison]) =>
			loaded ? false : detectOverwrite(extraction, comparison)
		)
		this.invalidNewGroups = vars.to(([loaded, extraction, comparison]) =>
			loaded ? [] : detectInvalidNewGroups(extraction.groups, this.study, comparison.groupMap)
		)
		return all([loaded, this.invalidOverwrite, this.invalidNewGroups] as const, (loaded, invalidOvewrite, invalidNewGroups) => {
			if (loaded) return validResult()
			const result = []
			// we are adding and every entered group does not already have a datapoint extracted for this outcome
			result.isValid = !invalidOvewrite
			if (invalidOvewrite) result.push('Data has already been extracted for this outcome. Select a new outcome, timepoint, or group to extract.')
			// EV-953 do not allow adding new groups
			result.isValid &&= !invalidNewGroups?.length
			if (invalidNewGroups?.length) {
				result.push(ExtractionModel.GROUP_ADD_DISALLOWED)
			}
			return result
		})
	}

	/**
	 * Reinitialize (load) from an extraction update
	 */
	reinit(extractionUpdate /* Extraction update */) {
		// force teardown and rebuild of UI
		const loaded = loadExtraction(this.analysisType, this.study, extractionUpdate)
		this.initExtraction(loaded)

		if (extractionUpdate.IsFromHistory == true)
			this.fromHistory.put(true)

		this.summary.reinit(extractionUpdate)
	}

	/**
	 * Clear the extraction
	 */
	clearValues() {
		this.summary.annotations.then(annotations => {
			const loaded = loadResults(this.analysisType, null, null, this.study.searchedOutcomes, this.study.searchedItvns, this.summary.studyCtx.allOutcomes, annotations)
			this.initExtraction(loaded, true)
		})
	}
}